import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { useState, useEffect } from 'react';
import { Menu, MenuItem, ProSidebar } from 'react-pro-sidebar';
import 'react-pro-sidebar/dist/css/styles.css';
import { Link, useLocation } from 'react-router-dom';
import { colorTheme } from 'theme';
import Diversity3OutlinedIcon from '@mui/icons-material/Diversity3Outlined';
import SavingsIcon from '@mui/icons-material/Savings';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArticleIcon from '@mui/icons-material/Article';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ChatIcon from '@mui/icons-material/Chat';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import SmsFailedIcon from '@mui/icons-material/SmsFailed';
import CalculateIcon from '@mui/icons-material/Calculate';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { type IDrawerLeftItemProps, type IDrawerLeftProps } from 'types';
import { privateRoutes } from '../../routes/Routes';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BuildIcon from '@mui/icons-material/Build';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

const SideBarIcon: Record<string, React.ReactNode> = {
  Home: <HomeOutlinedIcon />,
  Users: <PeopleOutlinedIcon />,
  Employees: <PeopleIcon />,
  Contributions: <ArticleIcon />,
  Admins: <AdminPanelSettingsIcon />,
  Customers: <Diversity3OutlinedIcon />,
  Orders: <ReceiptLongIcon />,
  'Fund Management': <SavingsIcon />,
  Onboarding: <PublishedWithChangesIcon />,
  Complaints: <SupportAgentOutlinedIcon />,
  Logout: <LogoutOutlinedIcon />,
  Employers: <PeopleOutlinedIcon />,
  Uploads: <UploadFileIcon />,
  Chats: <ChatIcon />,
  Requests: <FormatListBulletedIcon />,
  Transfers: <ImportExportIcon />,
  'Charge Calculation': <CalculateIcon />,
  'User Audit': <PeopleIcon />,
  'Employee Audit': <AssignmentIndIcon />,
  'Complaint Audit': <SmsFailedIcon />,
  'Chat Audit': <ChatIcon />,
  'Transaction Audit': <SyncAltIcon />,
  'Batch Jobs': <ArticleIcon />,
  'Daily Fund Price': <CalculateIcon />,
  Report: <AssessmentIcon />,
  'Daily Reports': <AssessmentIcon />,
  Configuration: <BuildIcon />,
  'Units Reallocation': <SwapHorizIcon />,
};

const Item = ({
  title,
  to,
  icon,
  selected,
  setSelected,
  openLogoutModal,
}: IDrawerLeftItemProps): JSX.Element => {
  const location = useLocation();
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  return (
    <MenuItem
      active={location.pathname === to}
      style={{
        color: colors.gray[100],
        fontFamily: 'Inter',
        fontWeight: 90,
      }}
      onClick={() => {
        if (title === 'Logout') {
          openLogoutModal();
          return;
        }
        setSelected(title);
      }}
      icon={icon}
    >
      <Typography sx={{ fontFamily: 'Inter', fontWeight: 500 }}>
        {title}
      </Typography>
      {title !== 'Logout' && <Link to={String(to)} />}
    </MenuItem>
  );
};

const DrawerLeft = ({ openLogoutModal }: IDrawerLeftProps): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [selected, setSelected] = useState<string>('');
  const routerName = useLocation();

  useEffect(() => {
    setSelected(routerName.pathname);
  }, [selected]);
  return (
    <Box
      sx={{
        '& .pro-sidebar-inner': {
          background: `${colors.primaryBg[900]} !important`,
          boxShadow: 3,
        },
        '& .pro-icon-wrapper': {
          backgroundColor: 'transparent !important',
        },
        '& .pro-inner-item': {
          padding: '5px 35px 5px 20px !important',
          fontFamily: 'Inter !important',
          fontWeight: 500,
          fontSize: 34,

          '& .pro-icon': {
            color: '#898989',
          },
        },
        '& .pro-inner-item: hover': {
          color: '#000000 !important',

          '& .pro-icon': {
            color: '#B5DFC8 !important',
          },
        },
        '& .pro-menu-item.active': {
          backgroundColor: '#B5DFC8 !important',
          color: '#000000 !important',
          borderRadius: '5px',

          width: '95%',
          fontFamily: 'Inter',
          '& .pro-icon': {
            color: '#000000 !important',
          },
        },
      }}
    >
      <ProSidebar collapsed={isCollapsed}>
        <Menu iconShape="square">
          {/* LOGO AND MENU ICON */}
          <MenuItem
            onClick={() => {
              setIsCollapsed(!isCollapsed);
            }}
            icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
            style={{
              margin: '10px 0 20px 0',
              color: colors.gray[100],
            }}
          >
            {!isCollapsed && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="7px"
              >
                <IconButton
                  onClick={() => {
                    setIsCollapsed(!isCollapsed);
                  }}
                >
                  <MenuOutlinedIcon />
                </IconButton>
                <Typography
                  style={{
                    fontWeight: 700,
                    color: colors.greenAccent[500],
                  }}
                  variant="h4"
                >
                  ZEN PENSIONS
                </Typography>
                <Typography variant="h3" color={colors.gray[100]}></Typography>
              </Box>
            )}
          </MenuItem>
          <Box paddingLeft={isCollapsed ? undefined : '4%'}>
            {privateRoutes.map(({ path, name }, index) => {
              if (
                name &&
                name !== 'UserPlanContribution' &&
                name !== 'UserWiseContribution'
              ) {
                const icon = SideBarIcon[name];

                return (
                  <Item
                    key={`${String(index)}-${String(name)}`}
                    title={name}
                    to={path}
                    icon={icon}
                    selected={selected}
                    setSelected={setSelected}
                    openLogoutModal={openLogoutModal}
                  />
                );
              }
              return null;
            })}
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default DrawerLeft;
